import { type FC, type ReactNode } from "react";

import {
  AssistantScope,
  type DocumentFragment,
  DocumentKindEnum,
  type DraftTemplateRoleEnum,
  type ProjectFragment,
} from "@app_schema";

import { ErrorLine } from "@styled/error_line";
import { NoticePanel } from "@styled/notice_panel";

import {
  DraftingDefaultStart,
  DraftingTemplateOwner,
} from "./drafting_default_start";
import { DraftingUploadChoice } from "./drafting_upload_choice";
import { DRAFT_COPY } from "./final_report_pane";
import { ReportSection } from "./report_section";
import { useDraftingStart } from "./use_drafting_start";

type DraftingProject = ProjectFragment & {
  hasDraftTemplate: boolean;
  draftingStartable: boolean;
  draftingReading: boolean;
};

type DraftSave = (
  blob: { signed_id: string },
  kind: DocumentKindEnum,
  role?: DraftTemplateRoleEnum,
) => Promise<void>;

// What the tab shows with no draft once he has a template: a case that has
// never had one starts from a copy of it; otherwise a file he uploads is for
// this case only or his new template, as he says. Nothing before his template.
const useDraftingEmpty = (project: DraftingProject, save: DraftSave) => {
  const startError = useDraftingStart(project.id, project.draftingStartable);

  if (project.draftingStartable) {
    return (
      <NoticePanel>
        Starting from your template…
        {startError && <ErrorLine>{startError}</ErrorLine>}
      </NoticePanel>
    );
  }
  if (!project.hasDraftTemplate) return undefined;

  return (
    <DraftingUploadChoice
      save={(blob, role) => save(blob, DocumentKindEnum.ReportDraft, role)}
    />
  );
};

// The Drafting tab.
export const DraftingTab: FC<{
  project: DraftingProject;
  agentId?: string;
  documents: DocumentFragment[];
  available: boolean;
  showAssistant: boolean;
  save: DraftSave;
}> = ({ project, agentId, documents, available, showAssistant, save }) => (
  <ReportSection
    project={project}
    scope={AssistantScope.Drafting}
    agentId={agentId}
    documents={documents}
    save={(blob) => save(blob, DocumentKindEnum.ReportDraft)}
    available={available}
    copy={DRAFT_COPY}
    showAssistant={showAssistant}
    drafting={{
      empty: useDraftingEmpty(project, save),
      secondary: !project.hasDraftTemplate && (
        <DraftingDefaultStart projectId={project.id} />
      ),
      footnote: <DraftingTemplateOwner clinician={project.user.name} />,
      reading: project.draftingReading,
    }}
  />
);

// Drafting on a case with no files yet: the same, else `fallback`.
export const GuidedDraftingPanel: FC<{
  project: DraftingProject;
  save: DraftSave;
  fallback: ReactNode;
}> = ({ project, save, fallback }) =>
  useDraftingEmpty(project, save) ?? fallback;
